import { ipaToFeatures, IpaType } from "../phonemes/ipa";
import { segmentPhonemes } from "../phonemes/segmentPhonemes";
import { millisecondsToSynth } from "../speak/exact";
import { CompiledRule } from "./compileRule";
import { compileRuleSet } from "./compileRuleSet";
import { SegmentStrategy } from "./intoSegments";
import { AddLengthRule, LengthRules, PhoneLengths } from "./lengthRules";
import { Rule } from "./rule";
import { CompiledRuleSet } from "./ruleSet";

/** make compiled rules that add lengths to phonemes in IPA+ (which includes lengths in milliseconds) */
export function compileLengthRules(lengthRules?: LengthRules): PhoneLengths {
  const rules = compileLengths(lengthRules)
  const compiledLengths: CompiledRuleSet = {
    inputSegments: SegmentStrategy.IpaPlus, outputSegments: SegmentStrategy.IpaPlus,
    lexicalSetLookup: [], phonotactics: [],
    rules,
  };

  if (lengthRules === undefined || lengthRules.defaultDiphthongMS === undefined) {
    return { rules: compiledLengths };
  }

  const asSynth = millisecondsToSynth(lengthRules?.defaultDiphthongMS)
  const defaultDiphthong = Number.parseInt(asSynth);
  return { rules: compiledLengths, defaultDiphthong };
}

function compileLengths(lengthRules?: LengthRules): CompiledRule[] {
  if (lengthRules === undefined) {
    return [];
  }

  const allIpa = Object.getOwnPropertyNames(lengthRules.map);
  const rules = allIpa.flatMap(ipa => lengthToRules(ipa, lengthRules.map[ipa], lengthRules.adds));

  const ruleSet = { rules, sortRulesByInputLength: true, outputSegments: SegmentStrategy.IpaPlus };
  const [compiled] = compileRuleSet(ruleSet);

  // need to insert rules at the beginning to avoid the rules getting sorted to the back
  const extraRules = rulesToAvoidSplits(allIpa);
  return extraRules.concat(compiled.rules);
}

/**
 * create a rule that modifies 'ipa' appropriately so it ends up with the given length (ms).
 * it adds additional rules if 'adds',
 * and it may add a final rule to mark 'ipa' as changed so later rules don't modify a portion of it.
 */
export function lengthToRules(ipa: IpaType, length: number, adds?: AddLengthRule[]): Rule[] {
  // output shouldn't include IPA length modifiers
  const stripLength = ipa.replaceAll(/ː|ˑ|'\u0306'/g, '');
  // break into pieces that can each have a length, like diphthongs
  const chunks = makeChunks(stripLength);

  // make rules that add in milliseconds & use specific context
  const rules: Rule[] = [];
  if (adds) {
    for (const add of adds) {
      const to = makeOutput(chunks, length + add.addMs);
      // combine custom 'next' with !length
      const next = !add.next ? { not: ':length' }
        : { and: [add.next, { not: ':length' }] };
      rules.push({ from: ipa, to, previous: add.previous, next, syllable: add.syllable });
    }
  }

  // default if none of the specific rules applied
  const to = makeOutput(chunks, length);
  rules.push({ from: ipa, to, next: { not: ':length' } });
  // this last rule says to leave /ipa/ as-is by marking it as changed,
  // e.g., the /ɛ/ in /ɛɪ̯/ shouldn't get a length added to it by a later rule
  if (ipa.length > 1) {
    rules.push({ from: ipa, to: ipa });
  }
  return rules;
}

/** a phoneme that can be assigned a length, e.g., with diacritics or a diphthong */
interface LengthChunk {
  readonly phoneme: IpaType;
  readonly length?: number;   /** if set, fixed length */
}

/** turn into chunks that each get their own length, e.g., di/triphthongs */
function makeChunks(ipa: IpaType): LengthChunk[] {
  const chunk: LengthChunk[] = [];
  const segments = segmentPhonemes(ipa).phonemes;
  let buildingChunk = false;
  for (const p of segments) {
    const phoneme = p.phoneme
    const features = ipaToFeatures(phoneme) ?? [];
    const isVowel = features.includes('vowel');
    const isNonSV = isVowel && features.includes('non-syllabic');
    const isApprox = !isVowel && (features.includes('approximant') || features.includes('lateral-approximant'));
    const isSyll = !isVowel && features.includes('syllabic');
    if (buildingChunk && (isNonSV || isApprox)) {
      // add a non-syllabic vowel or approximant to the current last chunk
      chunk[chunk.length - 1] = { phoneme: chunk[chunk.length - 1].phoneme + phoneme };
    } else if (isVowel && features.includes('extra-short')) {
      chunk.push({ phoneme, length: 50 });
    } else if (isVowel || isSyll) {
      // vowels & syllabic consonants need a length added on
      chunk.push({ phoneme });
    } else {
      chunk.push({ phoneme, length: 50 });
    }
    buildingChunk = isVowel;
  }
  return chunk;
}

/** roll 'length' into 'chunks' to produce the resulting IPA */
function makeOutput(chunks: LengthChunk[], length: number): string {
  const without = chunks.reduce((prev, curr) => prev += curr.length ? 0 : 1, 0);
  const fixed = chunks.reduce((prev, curr) => prev += curr.length ?? 0, 0);
  // length of each chunk that's not a fixed length
  const perChunk = without === 0 ? 0 : Math.floor((length - fixed) / without);

  // build up output string w/ possibly multiple embedded lengths
  const each = chunks.map(c => c.length ? c.phoneme : `${c.phoneme}${perChunk}`);
  return each.join('');
}

/** e.g., we don't want to add length to /aˈɹ/ so we add a rule ˈɹ > ˈɹ */
function rulesToAvoidSplits(allIpa: string[]): CompiledRule[] {
  const ends = getEnds(allIpa);
  const rules: Rule[] = [];
  for (const end of ends) {
    rules.push({ from: `ˈ${end}`, to: `ˈ${end}` });
    rules.push({ from: `ˌ${end}`, to: `ˌ${end}` });
  }

  const ruleSet = { rules, sortRulesByInputLength: true, outputSegments: SegmentStrategy.IpaPlus };
  const [compiled] = compileRuleSet(ruleSet);
  return compiled.rules;
}

/** get a list of all the ending phonemes */
function getEnds(allIpa: string[]): string[] {
  const unique: Set<string> = new Set();
  for (const ipa of allIpa) {
    const phonemes = segmentPhonemes(ipa).phonemes;
    if (phonemes.length >= 2) {
      const end = phonemes[phonemes.length - 1].phoneme;
      const features = ipaToFeatures(end);
      if (features) {
        const isVowel = features.includes('vowel');
        // if not a vowel, or a vowel not marked as part of a diphthong
        if (!isVowel || (isVowel && !features.includes('non-syllabic'))) {
          unique.add(end);
        }
      }
    }
  }

  const ends: string[] = [];
  unique.forEach(u => ends.push(u));
  return ends;
}
